import { toast } from 'sonner';
import {
  CheckCircle2, AlertTriangle, Sparkles, Phone, Calendar,
  ScanLine, Shield, Camera, ArrowRight, ExternalLink,
  DollarSign, Send, MessageCircle,
} from 'lucide-react';
import { C, STAGES, pColor } from './frontDeskData';

export const PatientDetailView = ({ patient: p, goOverview, advanceStage, openPayment, navigate }) => {
  const stageIdx = STAGES.findIndex(s => s.key === p.stage);
  const hasOutstandingCopay = p.copay.amount > 0 && !p.copay.collected;
  const nextAction = p.stage === 'scheduled' ? 'Check In' : p.stage === 'arrived' ? 'Move to Provider' : p.stage === 'with_provider' ? 'Check Out' : null;

  return (
    <div style={{ padding: 20 }} data-testid="fd-patient-detail">
      {/* Header */}
      <div style={{ background: '#fff', borderRadius: 10, border: '.5px solid #e8e8e8', padding: '14px 18px', marginBottom: 14 }} data-testid="fd-patient-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          <button onClick={goOverview} style={{ fontSize: 10, color: C.purple, background: 'none', border: 'none', cursor: 'pointer' }} data-testid="fd-back-overview">&larr; Overview</button>
          <div style={{ flex: 1 }} />
          {hasOutstandingCopay && (
            <button onClick={() => openPayment(p.id)}
              style={{ height: 34, padding: '0 14px', borderRadius: 6, background: C.black, color: '#fff', fontSize: 11, fontWeight: 500, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}
              data-testid="fd-collect-copay-btn">
              <DollarSign style={{ width: 12, height: 12 }} /> Collect ${p.copay.amount}
            </button>
          )}
          {nextAction && (
            <button onClick={() => advanceStage(p.id)}
              style={{ height: 34, padding: '0 14px', borderRadius: 6, background: p.stage === 'scheduled' ? C.greenBtn : C.black, color: '#fff', fontSize: 11, fontWeight: 500, border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}
              data-testid="fd-advance-btn">
              <ArrowRight style={{ width: 12, height: 12 }} /> {nextAction}
            </button>
          )}
          <button onClick={() => navigate('/lumina/patients/1')}
            style={{ height: 34, padding: '0 12px', borderRadius: 6, background: '#fff', color: '#555', fontSize: 11, border: '.5px solid #e0e0e0', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}
            data-testid="fd-view-lumina-btn">
            <ExternalLink style={{ width: 10, height: 10 }} /> View chart in Lumina &rarr;
          </button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#e8e8e8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, color: '#555' }}>
            {p.name.split(' ').map(w => w[0]).join('')}
          </div>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 15, fontWeight: 500, color: C.black }}>{p.name}</span>
              <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: '#f0f0f0', color: '#888' }}>{p.mrn}</span>
              {p.fhirConnected && <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: '#eff6ff', color: '#3b82f6', fontWeight: 500 }}>FHIR</span>}
            </div>
            <p style={{ fontSize: 11, color: '#888' }}>DOB: {p.dob} &middot; {p.sex} &middot; {p.time} {p.type} &middot; {p.provider}</p>
          </div>
        </div>
        {/* Flow strip */}
        <div style={{ background: '#fafafa', borderTop: '.5px solid #f0f0f0', margin: '10px -18px -14px', padding: '8px 18px' }} data-testid="fd-flow-strip">
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {STAGES.map((stg, idx) => (
              <div key={stg.key} style={{ display: 'flex', alignItems: 'center', gap: 4, flex: 1 }}>
                <div style={{ width: 18, height: 18, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 600, background: idx < stageIdx ? C.black : idx === stageIdx ? C.purple : '#e8e8e8', color: idx <= stageIdx ? '#fff' : '#aaa' }}>
                  {idx < stageIdx ? <CheckCircle2 style={{ width: 10, height: 10 }} /> : idx + 1}
                </div>
                <span style={{ fontSize: 10, fontWeight: idx === stageIdx ? 500 : 400, color: idx === stageIdx ? C.purple : '#aaa' }}>{stg.label}</span>
                {idx < STAGES.length - 1 && <div style={{ flex: 1, height: 1, background: idx < stageIdx ? C.black : '#e8e8e8', margin: '0 4px' }} />}
              </div>
            ))}
          </div>
          {p.arrivedAt && stageIdx >= 1 && <p style={{ fontSize: 9, color: C.purple, marginTop: 3 }}>Arrived {p.arrivedAt}</p>}
        </div>
      </div>

      {/* Cards grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        {/* Card 1: Echo AI Actions (always first) */}
        <div style={{ gridColumn: '1 / -1', background: '#fff', borderRadius: 10, border: '.5px solid #e0e0e0', overflow: 'hidden' }} data-testid="fd-ai-actions-card">
          <div style={{ padding: '10px 14px', borderBottom: '.5px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Sparkles style={{ width: 14, height: 14, color: C.purple }} />
            <span style={{ fontSize: 13, fontWeight: 500, color: C.black }}>Echo AI &middot; {p.aiActions.length} actions</span>
            <span style={{ fontSize: 9, color: '#aaa', marginLeft: 'auto' }}>Updated just now</span>
          </div>
          <div>
            {p.aiActions.map((action, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '9px 14px', borderBottom: i < p.aiActions.length - 1 ? '.5px solid #f5f5f5' : 'none' }} data-testid={`fd-ai-action-${i}`}>
                <div style={{ width: 16, height: 16, borderRadius: '50%', background: action.done ? C.green : pColor(action.priority), flexShrink: 0, marginTop: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {action.done && <CheckCircle2 style={{ width: 9, height: 9, color: '#fff' }} />}
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: 12, fontWeight: 500, color: C.black }}>{action.title}</p>
                  <p style={{ fontSize: 11, color: '#555' }}>{action.detail}</p>
                  {action.financial && <p style={{ fontSize: 10, color: '#aaa' }}>{action.financial}</p>}
                </div>
                {!action.done && action.priority !== 'blue' && (
                  action.title.includes('Collect') ? (
                    <button onClick={() => openPayment(p.id)} style={{ fontSize: 10, background: C.black, color: '#fff', border: 'none', borderRadius: 4, padding: '3px 8px', cursor: 'pointer', fontWeight: 500 }}>Collect</button>
                  ) : (
                    <button onClick={() => toast.info(`Action: ${action.title}`)} style={{ fontSize: 10, color: '#555', background: '#f5f5f5', border: '.5px solid #e8e8e8', borderRadius: 4, padding: '3px 8px', cursor: 'pointer' }}>
                      {action.title.includes('insurance') || action.title.includes('Insurance') ? 'Scan ID' : action.title.includes('Intake') || action.title.includes('intake') ? 'Send form' : action.title.includes('risk') ? 'Call' : 'Resolve'}
                    </button>
                  )
                )}
                {action.done && <CheckCircle2 style={{ width: 14, height: 14, color: C.green, flexShrink: 0 }} />}
              </div>
            ))}
            {p.aiActions.length === 0 && (
              <div style={{ padding: 16, textAlign: 'center', fontSize: 10, color: '#555', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <CheckCircle2 style={{ width: 14, height: 14, color: C.green }} /> All clear for this visit &mdash; Echo AI is ready
              </div>
            )}
          </div>
        </div>

        {/* Card 2: Patient & Insurance */}
        <div style={{ background: '#fff', borderRadius: 10, border: '.5px solid #e8e8e8', overflow: 'hidden' }} data-testid="fd-patient-insurance-card">
          <div style={{ padding: '10px 14px', borderBottom: '.5px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Shield style={{ width: 14, height: 14, color: '#555' }} />
            <span style={{ fontSize: 13, fontWeight: 500, color: C.black }}>Patient & Insurance</span>
          </div>
          <div style={{ padding: 14 }}>
            {p.fhirConnected && p.insurance ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 10, padding: '4px 8px', borderRadius: 5, background: p.insurance.verified ? '#f0fdf4' : '#fffbeb', border: `.5px solid ${p.insurance.verified ? '#bbf7d0' : '#fde68a'}` }}>
                  {p.insurance.verified ? <CheckCircle2 style={{ width: 12, height: 12, color: C.green }} /> : <AlertTriangle style={{ width: 12, height: 12, color: C.amber }} />}
                  <span style={{ fontSize: 10, color: p.insurance.verified ? C.green : '#854F0B' }}>
                    {p.insurance.verified ? `Insurance verified by Echo AI \u00b7 ${p.insurance.verifiedAt}` : 'Verification pending'}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-2" style={{ fontSize: 12 }}>
                  {[
                    { l: 'Payer', v: p.insurance.payer }, { l: 'Member ID', v: p.insurance.memberId },
                    { l: 'Group', v: p.insurance.group }, { l: 'Copay', v: p.insurance.copay },
                    { l: 'Status', v: p.insurance.status },
                  ].map((f, i) => (<div key={i}><p style={{ fontSize: 9, color: '#aaa' }}>{f.l}</p><p style={{ color: C.black }}>{f.v}</p></div>))}
                </div>
                {p.demographics && <>
                  <div style={{ height: .5, background: '#f0f0f0', margin: '10px 0' }} />
                  <div className="grid grid-cols-2 gap-2" style={{ fontSize: 12 }}>
                    {[
                      { l: 'Phone', v: p.demographics.phone }, { l: 'Email', v: p.demographics.email || '\u2014' },
                      { l: 'Address', v: p.demographics.address || '\u2014' }, { l: 'Emergency', v: p.demographics.emergencyContact || '\u2014' },
                    ].map((f, i) => (<div key={i}><p style={{ fontSize: 9, color: '#aaa' }}>{f.l}</p><p style={{ color: C.black }}>{f.v}</p></div>))}
                  </div>
                </>}
                {p.allergies?.length > 0 && <>
                  <div style={{ height: .5, background: '#f0f0f0', margin: '10px 0' }} />
                  <p style={{ fontSize: 9, color: '#aaa', marginBottom: 3 }}>ALLERGIES</p>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {p.allergies.map((a, i) => <span key={i} style={{ fontSize: 10, padding: '1px 7px', borderRadius: 10, background: '#fef2f2', color: C.red }}>{a}</span>)}
                  </div>
                </>}
              </>
            ) : (
              <div style={{ textAlign: 'center', padding: '20px 0' }} data-testid="fd-lens-prompt">
                <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 10, padding: '4px 8px', borderRadius: 5, background: '#fffbeb', border: '.5px solid #fde68a', justifyContent: 'center' }}>
                  <AlertTriangle style={{ width: 12, height: 12, color: C.amber }} />
                  <span style={{ fontSize: 10, color: '#854F0B' }}>Verification pending &mdash; scan insurance card</span>
                </div>
                <ScanLine style={{ width: 24, height: 24, color: C.red, margin: '0 auto 8px' }} />
                <p style={{ fontSize: 13, fontWeight: 500, color: C.black, marginBottom: 3 }}>No records on file</p>
                <p style={{ fontSize: 11, color: '#888', marginBottom: 12 }}>Scan ID and insurance card to populate</p>
                <button onClick={() => toast.info('Lens opening for ' + p.name)}
                  style={{ height: 32, padding: '0 16px', borderRadius: 6, background: C.black, color: '#fff', fontSize: 11, fontWeight: 500, border: 'none', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5 }}
                  data-testid="fd-scan-patient-btn"><Camera style={{ width: 12, height: 12 }} /> Scan ID & Insurance</button>
              </div>
            )}
          </div>
        </div>

        {/* Card 3: Communications */}
        <div style={{ background: '#fff', borderRadius: 10, border: '.5px solid #e8e8e8', overflow: 'hidden' }} data-testid="fd-comms-card">
          <div style={{ padding: '10px 14px', borderBottom: '.5px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 6 }}>
            <MessageCircle style={{ width: 14, height: 14, color: '#555' }} />
            <span style={{ fontSize: 13, fontWeight: 500, color: C.black }}>Communications</span>
            <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 3, background: '#f0f0f0', color: '#888' }}>Today</span>
            <button onClick={() => toast.info('Opening Comms Hub')} style={{ fontSize: 10, color: C.purple, background: 'none', border: 'none', cursor: 'pointer', marginLeft: 'auto' }}>View all &rarr;</button>
          </div>
          <div style={{ padding: 14 }}>
            {p.comms.slice(0, 4).map((c, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 8 }} data-testid={`fd-comm-${i}`}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: c.type === 'ai' ? C.purple : '#d1d5db', flexShrink: 0, marginTop: 5 }} />
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: 11, color: '#333' }}>{c.text}</p>
                  <p style={{ fontSize: 9, color: '#aaa' }}>{c.time}</p>
                </div>
                {c.type === 'ai' && <span style={{ fontSize: 9, color: C.purple, background: `${C.purple}12`, padding: '1px 5px', borderRadius: 3 }}>AI</span>}
              </div>
            ))}
            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
              <button onClick={() => toast.info('Send message')} style={{ flex: 1, height: 28, borderRadius: 5, background: '#f5f5f5', border: '.5px solid #e8e8e8', color: '#555', fontSize: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                <Send style={{ width: 10, height: 10 }} /> Send message
              </button>
              <button onClick={() => toast.info('Calling patient')} style={{ flex: 1, height: 28, borderRadius: 5, background: '#f5f5f5', border: '.5px solid #e8e8e8', color: '#555', fontSize: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                <Phone style={{ width: 10, height: 10 }} /> Call patient
              </button>
            </div>
          </div>
        </div>

        {/* Card 4: Schedule & Follow-up */}
        <div style={{ background: '#fff', borderRadius: 10, border: '.5px solid #e8e8e8', overflow: 'hidden' }} data-testid="fd-schedule-card">
          <div style={{ padding: '10px 14px', borderBottom: '.5px solid #f0f0f0', display: 'flex', alignItems: 'center', gap: 6 }}>
            <Calendar style={{ width: 14, height: 14, color: '#555' }} />
            <span style={{ fontSize: 13, fontWeight: 500, color: C.black }}>Schedule & Follow-up</span>
          </div>
          <div style={{ padding: 14 }}>
            <p style={{ fontSize: 9, color: '#aaa', textTransform: 'uppercase', marginBottom: 3 }}>Current visit</p>
            <p style={{ fontSize: 12, color: C.black }}><strong>{p.time}</strong> &middot; {p.type} &middot; {p.provider}</p>
            <div style={{ height: .5, background: '#f0f0f0', margin: '10px 0' }} />
            <p style={{ fontSize: 9, color: '#aaa', textTransform: 'uppercase', marginBottom: 3 }}>Upcoming</p>
            {p.nextAppts.length > 0 ? p.nextAppts.map((a, i) => (
              <p key={i} style={{ fontSize: 11, color: '#333', padding: '2px 0' }}>{a.date} at {a.time} &middot; {a.type} &middot; {a.provider}</p>
            )) : (
              <>
                <p style={{ fontSize: 11, color: '#999', marginBottom: 6 }}>None scheduled</p>
                <button onClick={() => toast.info('Scheduling follow-up')}
                  style={{ height: 26, padding: '0 10px', borderRadius: 5, background: '#f5f5f5', border: '.5px solid #e8e8e8', color: '#555', fontSize: 10, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <Calendar style={{ width: 10, height: 10 }} /> Schedule Follow-up
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
